/**
 * useActionstepSession
 *
 * Shared hook that manages a single Actionstep OAuth session across all VALS
 * tools. Stores the access token + API endpoint in sessionStorage so the user
 * only has to sign in once per browser session.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  buildActionstepAuthorizeUrl,
  clearActionstepOAuthDraft,
  exchangeActionstepCode,
  loadActionstepOAuthDraft,
  saveActionstepOAuthDraft,
  searchActionstepMatters,
  createActionstepFileNote,
  refreshActionstepToken,
  isActionstepTokenExpired,
  uploadActionstepMatterDocument,
  ACTIONSTEP_ENVIRONMENTS,
  DEFAULT_ACTIONSTEP_ENVIRONMENT,
  ACTIONSTEP_SCOPES,
} from "@/lib/aidPlannerAdapters/actionstepClient";
import { actionstepSimulatorStore } from "@/lib/actionstepSimulatorStore";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";

const SESSION_KEY = "vals_actionstep_session";

function loadSession() {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSession(session) {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // ignore
  }
}

function clearSession() {
  window.sessionStorage.removeItem(SESSION_KEY);
}

/**
 * @returns {{
 *   session: { accessToken: string, apiEndpoint: string } | null,
 *   isConnected: boolean,
 *   connect: (opts: { clientId: string, clientSecret: string, redirectUri: string, environment: string }) => void,
 *   disconnect: () => void,
 *   exchangeCode: (opts: { clientId: string, clientSecret: string, redirectUri: string, environment: string, code: string }) => Promise<void>,
 *   searchMatters: (query: string) => Promise<Array>,
 *   saveFileNote: (actionId: string, text: string) => Promise<void>,
 *   isExchanging: boolean,
 *   isSearching: boolean,
 *   isSaving: boolean,
 *   exchangeError: string,
 *   searchError: string,
 *   saveError: string,
 * }}
 */
export function useActionstepSession() {
  const simulatorMode = isLacwLocalTestMode();
  const simulatorSession = { accessToken: "local-simulator", apiEndpoint: "local-actionstep-simulator", simulated: true };
  const [session, setSession] = useState(() => simulatorMode ? simulatorSession : loadSession());
  // A ref so the refresh wrapper always reads the current session without having to
  // be rebuilt whenever it changes.
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);
  const [isExchanging, setIsExchanging] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [exchangeError, setExchangeError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // On mount: try to handle OAuth redirect code automatically
  useEffect(() => {
    if (simulatorMode) return;
    const params = new URLSearchParams(window.location.search);
    const returnedCode = params.get("code");
    if (!returnedCode) return;

    const draft = loadActionstepOAuthDraft();
    if (!draft?.clientId || !draft?.clientSecret || !draft?.redirectUri) return;

    // Exchange the code automatically
    (async () => {
      setIsExchanging(true);
      setExchangeError("");
      try {
        const tokenData = await exchangeActionstepCode({
          environment: draft.environment || DEFAULT_ACTIONSTEP_ENVIRONMENT,
          clientId: draft.clientId,
          clientSecret: draft.clientSecret,
          redirectUri: draft.redirectUri,
          code: returnedCode,
        });

        const newSession = {
          accessToken: tokenData.access_token || "",
          apiEndpoint: tokenData.api_endpoint || "",
          // Kept so an expired access token can be refreshed silently instead of ending
          // the session mid-task. All of it lives in sessionStorage and dies with the tab,
          // which is the same exposure the sign-in form already has.
          refreshToken: tokenData.refresh_token || "",
          environment: draft.environment || DEFAULT_ACTIONSTEP_ENVIRONMENT,
          clientId: draft.clientId,
          clientSecret: draft.clientSecret,
        };
        saveSession(newSession);
        setSession(newSession);
        clearActionstepOAuthDraft();

        // Clean the ?code= from the URL
        params.delete("code");
        const nextQuery = params.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}`;
        window.history.replaceState({}, "", nextUrl);
      } catch (err) {
        setExchangeError(err instanceof Error ? err.message : "Token exchange failed.");
      } finally {
        setIsExchanging(false);
      }
    })();
  }, [simulatorMode]);

  // Sign-in happens in a popup, not by navigating this window.
  //
  // The portal runs inside an iframe on completelawsupport.com, and Actionstep's authorize
  // page sets frame-ancestors that do not include that origin. Redirecting the iframe to
  // Actionstep therefore renders a broken page - no credential or redirect URI can fix it,
  // because the browser refuses to display the page at all. A popup is a top-level window
  // and is not subject to framing rules.
  //
  // The popup lands on /actionstep-auth.html, which posts the code back here and closes.
  const connect = useCallback(({ clientId, clientSecret, redirectUri, environment = DEFAULT_ACTIONSTEP_ENVIRONMENT }) => {
    if (simulatorMode) {
      setSession(simulatorSession);
      return;
    }
    if (!clientId?.trim() || !redirectUri?.trim()) {
      setExchangeError("Client ID and Redirect URI are required.");
      return;
    }
    setExchangeError("");
    saveActionstepOAuthDraft({ environment, clientId, clientSecret, redirectUri, scopes: ACTIONSTEP_SCOPES });

    const authorizeUrl = buildActionstepAuthorizeUrl({
      environment,
      clientId,
      scopes: ACTIONSTEP_SCOPES,
      redirectUri,
    });

    const popup = window.open(authorizeUrl, "actionstep-oauth", "width=620,height=780");
    if (!popup) {
      // Browsers only permit a popup opened directly from a click. If one is blocked, say
      // so - a silent failure here looks identical to Actionstep being down.
      setExchangeError(
        "Your browser blocked the Actionstep sign-in window. Allow pop-ups for this site and try again."
      );
      return;
    }

    // A sign-in that never reports back used to leave the button waiting indefinitely,
    // which looks identical to Actionstep being slow. Whatever the cause - the window was
    // closed, or the callback could not reach us - it now ends in a sentence.
    let settled = false;
    const watch = window.setInterval(() => {
      if (settled) return;
      let closed = false;
      try { closed = popup.closed; } catch { closed = true; }
      if (!closed) return;
      settled = true;
      window.clearInterval(watch);
      window.removeEventListener("message", onMessage);
      setExchangeError(
        "The Actionstep sign-in window closed before it finished. Please try connecting again.",
      );
    }, 1000);

    const onMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "actionstep-oauth") return;
      settled = true;
      window.clearInterval(watch);
      window.removeEventListener("message", onMessage);
      try { popup.close(); } catch { /* already closed */ }

      if (event.data.error) {
        setExchangeError(event.data.error);
        return;
      }
      if (!event.data.code) {
        setExchangeError("Actionstep did not return an authorization code.");
        return;
      }

      setIsExchanging(true);
      try {
        const tokenData = await exchangeActionstepCode({
          environment,
          clientId,
          clientSecret,
          redirectUri,
          code: event.data.code,
        });
        const newSession = {
          accessToken: tokenData.access_token || "",
          apiEndpoint: tokenData.api_endpoint || "",
          // Kept so an expired access token can be refreshed silently instead of ending
          // the session mid-task. All of it lives in sessionStorage and dies with the tab,
          // which is the same exposure the sign-in form already has.
          refreshToken: tokenData.refresh_token || "",
          environment,
          clientId,
          clientSecret,
        };
        saveSession(newSession);
        setSession(newSession);
        clearActionstepOAuthDraft();
      } catch (err) {
        setExchangeError(err instanceof Error ? err.message : "Token exchange failed.");
      } finally {
        setIsExchanging(false);
      }
    };

    window.addEventListener("message", onMessage);
  }, [simulatorMode]);

  const disconnect = useCallback(() => {
    if (simulatorMode) {
      setSession(simulatorSession);
      return;
    }
    clearSession();
    clearActionstepOAuthDraft();
    setSession(null);
  }, [simulatorMode]);

  const exchangeCode = useCallback(async ({ clientId, clientSecret, redirectUri, environment = DEFAULT_ACTIONSTEP_ENVIRONMENT, code }) => {
    setIsExchanging(true);
    setExchangeError("");
    try {
      const tokenData = await exchangeActionstepCode({ environment, clientId, clientSecret, redirectUri, code });
      const newSession = {
        accessToken: tokenData.access_token || "",
        apiEndpoint: tokenData.api_endpoint || "",
        refreshToken: tokenData.refresh_token || "",
        environment,
        clientId,
        clientSecret,
      };
      saveSession(newSession);
      setSession(newSession);
      clearActionstepOAuthDraft();
    } catch (err) {
      setExchangeError(err instanceof Error ? err.message : "Token exchange failed.");
      throw err;
    } finally {
      setIsExchanging(false);
    }
  }, []);

  // Runs an Actionstep call, refreshing the access token once if it has expired.
  //
  // Without this the first expiry ended the session mid-task: a practitioner part-way
  // through saving a certification got a raw 401 and had to sign in again, losing what
  // they were doing. The refresh is silent and the call is retried once - once only,
  // because a second failure is not a stale token and retrying would just fail twice.
  const withFreshToken = useCallback(async (run) => {
    const current = sessionRef.current;
    try {
      return await run(current);
    } catch (err) {
      if (!isActionstepTokenExpired(err) || !current?.refreshToken) throw err;

      let refreshed;
      try {
        refreshed = await refreshActionstepToken({
          environment: current.environment || DEFAULT_ACTIONSTEP_ENVIRONMENT,
          clientId: current.clientId,
          clientSecret: current.clientSecret,
          refreshToken: current.refreshToken,
        });
      } catch {
        // The refresh token is spent or revoked. Clear the session so the UI offers a
        // sign-in rather than repeating a failure the user cannot act on.
        clearSession();
        setSession(null);
        throw new Error("Your Actionstep session has expired. Please connect again.");
      }

      const next = {
        ...current,
        accessToken: refreshed?.access_token || current.accessToken,
        apiEndpoint: refreshed?.api_endpoint || current.apiEndpoint,
        // Actionstep may issue a new refresh token; keep the existing one if it does not.
        refreshToken: refreshed?.refresh_token || current.refreshToken,
      };
      saveSession(next);
      setSession(next);
      sessionRef.current = next;
      return run(next);
    }
  }, []);

  const searchMatters = useCallback(async (query) => {
    if (!session?.accessToken || !session?.apiEndpoint) {
      throw new Error("Not connected to Actionstep. Please sign in first.");
    }
    setIsSearching(true);
    setSearchError("");
    try {
      if (simulatorMode) return actionstepSimulatorStore.searchMatters(query);
      const results = await withFreshToken((live) => searchActionstepMatters({
        apiEndpoint: live.apiEndpoint,
        accessToken: live.accessToken,
        query,
      }));
      return results;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Matter search failed.";
      setSearchError(msg);
      throw err;
    } finally {
      setIsSearching(false);
    }
  }, [session]);

  const saveFileNote = useCallback(async (actionId, text) => {
    if (!session?.accessToken || !session?.apiEndpoint) {
      throw new Error("Not connected to Actionstep. Please sign in first.");
    }
    setIsSaving(true);
    setSaveError("");
    try {
      if (simulatorMode) return actionstepSimulatorStore.createFileNote(actionId, text, "CLS Portal — LACW");
      await withFreshToken((live) => createActionstepFileNote({
        apiEndpoint: live.apiEndpoint,
        accessToken: live.accessToken,
        actionId,
        text,
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save file note.";
      setSaveError(msg);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [session, simulatorMode]);

  const uploadMatterDocument = useCallback(async (actionId, file, folderName = "") => {
    if (!session?.accessToken || !session?.apiEndpoint) {
      throw new Error("Not connected to Actionstep. Please sign in first.");
    }
    setIsUploading(true);
    setUploadError("");
    try {
      if (simulatorMode) {
        return actionstepSimulatorStore.createDocument(actionId, {
          name: file?.name || "Portal document",
          size: file?.size || 0,
          type: file?.type || "application/octet-stream",
        });
      }
      return await withFreshToken((live) => uploadActionstepMatterDocument({
        apiEndpoint: live.apiEndpoint,
        accessToken: live.accessToken,
        actionId,
        file,
        folderName,
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to upload the document.";
      setUploadError(msg);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, [session, simulatorMode]);

  return {
    session,
    isConnected: simulatorMode || !!(session?.accessToken && session?.apiEndpoint),
    isSimulator: simulatorMode,
    connect,
    disconnect,
    exchangeCode,
    searchMatters,
    saveFileNote,
    uploadMatterDocument,
    isExchanging,
    isSearching,
    isSaving,
    exchangeError,
    searchError,
    saveError,
    isUploading,
    uploadError,
  };
}
